import React, { FC } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Container, Grid, Hidden, Theme, Typography, useMediaQuery } from '@material-ui/core';
import {
  firstStepHIWImage,
  secondStepHIWImage,
  thirdStepHIWImage,
} from 'assets/images/led-landing';
import clsx from 'clsx';
import useI18n from "evcharging/hooks/useI18n";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      backgroundColor: theme.palette.secondary.main,
      padding: theme.spacing(14, 0),
    },
    whiteText: {
      color: theme.palette.common.white,
    },
    image: {
      '& img': {
        width: '100%',
        borderRadius: theme.spacing(3),
      },
    },
    imageContent: {
      '& img': {
        maxHeight: 260,
        objectFit: 'contain',
        objectPosition: 'left',
        display: 'block',
        marginBottom: theme.spacing(6),
      },
    },
    squareNumber: {
      backgroundColor: theme.palette.primary.main,
      border: '1px solid #EEEEEE',
      borderRadius: theme.spacing(2),
      color: theme.palette.common.white,
      width: 48,
      height: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: theme.spacing(10),
      [theme.breakpoints.up('sm')]: {
        marginTop: 'calc(260px / 2 - 24px)',
      },
      [theme.breakpoints.up('lg')]: {
        marginTop: 0,
      },
    },
    lineWrapper: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      minHeight: '100%',
      flexDirection: 'column',
      position: 'relative',
      [theme.breakpoints.up('sm')]: {
        alignItems: 'center',
      },
      [theme.breakpoints.up('lg')]: {
        justifyContent: 'center',
      },
    },
    line: {
      border: `1px dashed ${theme.palette.common.white}`,
      position: 'absolute',
      height: 'calc(100% - 118px)',
      left: 23,
      [theme.breakpoints.up('sm')]: {
        left: 'inherit',
        height: 'calc(100% - 240px / 2 - 56px)',
      },
      [theme.breakpoints.up('lg')]: {
        height: 'calc(50% - 44px)',
      },
    },
    lineBottom: {
      bottom: 0,
    },
    lineTop: {
      top: 2,
    },
    paddingFirst: {
      padding: theme.spacing(0, 0, 14),
      '& $lineBottom': {
        height: 'calc(100% - 118px)',
        [theme.breakpoints.up('sm')]: {
          height: 'calc(100% - 260px / 2 - 48px)',
        },
        [theme.breakpoints.up('lg')]: {
          height: 'calc(50% - 14px)',
        },
      },
    },
    paddingBetween: {
      padding: theme.spacing(0, 0, 14),
      '& $lineTop': {
        height: theme.spacing(6),
        [theme.breakpoints.between('sm', 'md')]: {
          height: 'calc(260px / 2 - 48px)',
        },
        [theme.breakpoints.up('lg')]: {
          height: 'calc(50% - 44px)',
        },
      },
      [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(14, 0),
      },
    },
    paddingLast: {
      '& $lineTop': {
        height: theme.spacing(6),
        [theme.breakpoints.between('sm', 'md')]: {
          height: 'calc(260px / 2 - 48px)',
        },
      },
      [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(14, 0, 0),
        '& $lineTop': {
          height: 'calc(50% - 14px)',
        },
      },
    },
    title: {
      marginBottom: theme.spacing(4),
      [theme.breakpoints.down(414)]: {
        fontSize: 25,
      },
      [theme.breakpoints.up('sm')]: {
        fontSize: 36,
      },
    },
  };
});

const HowItWorksSection: FC = () => {
  const { t } = useI18n();
  const classes = useStyles();
  const stages: Array<{ image: string; title: string; description: string }> = [
    {
      image: firstStepHIWImage,
      title: 'landing.how.step1',
      description: 'landing.how.step1-description',
    },
    {
      image: secondStepHIWImage,
      title: 'landing.how.step2',
      description: 'landing.how.step2-description',
    },
    {
      image: thirdStepHIWImage,
      title: 'landing.how.step3',
      description: 'landing.how.step3-description',
    },
  ];
  const isSmallUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  return (
    <Box className={classes.root}>
      <Container maxWidth={false} fixed>
        <Box mb={14}>
          <Typography variant="h2" align="center" className={classes.whiteText}>
            {t('landing.how')}
          </Typography>
        </Box>
        <Grid container justifyContent={isSmallUp ? 'center' : 'space-between'}>
          {stages.map((stage, index, arr) => (
            <React.Fragment key={index}>
              <Hidden mdDown>
                <Grid item lg={5}>
                  <Box
                    className={clsx(
                      classes.image,
                      index === 0
                        ? classes.paddingFirst
                        : index === arr.length - 1
                        ? classes.paddingLast
                        : classes.paddingBetween
                    )}
                  >
                    <img src={stage.image} alt={`${index + 1} step`} />
                  </Box>
                </Grid>
              </Hidden>
              <Grid item xs={2} md={2} lg={2}>
                <Box
                  className={clsx(
                    classes.lineWrapper,
                    index === 0
                      ? classes.paddingFirst
                      : index === arr.length - 1
                      ? classes.paddingLast
                      : classes.paddingBetween
                  )}
                >
                  {index !== 0 && <Box className={clsx(classes.line, classes.lineTop)} />}
                  <Typography variant="h4" component="span" className={classes.squareNumber}>
                    {index + 1}
                  </Typography>
                  {index !== arr.length - 1 && (
                    <Box className={clsx(classes.line, classes.lineBottom)} />
                  )}
                </Box>
              </Grid>
              <Grid item lg={4} md={9} xs={9}>
                <Box
                  className={clsx(
                    index === 0
                      ? classes.paddingFirst
                      : index === arr.length - 1
                      ? classes.paddingLast
                      : classes.paddingBetween
                  )}
                >
                  <Hidden lgUp>
                    <Box className={clsx(classes.image, classes.imageContent)}>
                      <img src={stage.image} alt={`${index + 1} step`} />
                    </Box>
                  </Hidden>
                  <Typography variant="h2" className={clsx(classes.whiteText, classes.title)}>
                    {t(stage.title)}
                  </Typography>
                  <Typography variant="h5" className={classes.whiteText}>
                    {t(stage.description)}
                  </Typography>
                </Box>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default HowItWorksSection;
